import { supabase } from '../lib/supabase'
import type {
  CreatePromptAgentePayload,
  PromptAgente,
  PromptAgenteFilters,
  PromptAgenteVersion,
  UpdatePromptAgentePayload,
} from '../types/promptsAgentes'

async function getMultipleUsersInfo(
  userIds: string[]
): Promise<Record<string, { name: string; email: string }>> {
  try {
    const { data, error } = await supabase.rpc('get_multiple_users_info', {
      user_ids: userIds,
    })

    if (error || !data) return {}

    const userMap: Record<string, { name: string; email: string }> = {}
    if (Array.isArray(data)) {
      data.forEach((user: { id: string; name?: string; email?: string }) => {
        userMap[user.id] = {
          name: user.name || 'Usuário',
          email: user.email || '',
        }
      })
    }
    return userMap
  } catch {
    return {}
  }
}

async function createPromptVersion(
  promptId: string,
  versionNumber: number,
  title: string,
  description: string | null,
  prompt: string,
  changeSummary?: string
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('Usuário não autenticado')
  }

  const { error } = await supabase.from('ai_agent_prompt_versions').insert({
    prompt_id: promptId,
    version_number: versionNumber,
    title,
    description,
    prompt,
    created_by: user.id,
    change_summary: changeSummary,
  })

  if (error) {
    throw new Error(`Erro ao criar versão: ${error.message}`)
  }
}

export async function getPromptVersions(promptId: string): Promise<PromptAgenteVersion[]> {
  const { data, error } = await supabase
    .from('ai_agent_prompt_versions')
    .select('*')
    .eq('prompt_id', promptId)
    .order('version_number', { ascending: false })

  if (error) {
    throw new Error(`Erro ao buscar versões: ${error.message}`)
  }

  const versions = (data || []) as PromptAgenteVersion[]
  const authorIds = Array.from(
    new Set(versions.map(v => v.created_by).filter(Boolean))
  ) as string[]

  let userMap: Record<string, { name: string; email: string }> = {}
  if (authorIds.length > 0) {
    userMap = await getMultipleUsersInfo(authorIds)
  }

  return versions.map(v => ({
    ...v,
    user_name: v.created_by ? userMap[v.created_by]?.name || 'Usuário' : 'Usuário',
  }))
}

export async function restorePromptVersion(
  promptId: string,
  versionNumber: number
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('Usuário não autenticado')
  }

  const { data: version, error: versionError } = await supabase
    .from('ai_agent_prompt_versions')
    .select('*')
    .eq('prompt_id', promptId)
    .eq('version_number', versionNumber)
    .single()

  if (versionError || !version) {
    throw new Error('Versão não encontrada')
  }

  const { error: updateError } = await supabase
    .from('ai_agent_prompts')
    .update({
      title: version.title,
      description: version.description,
      prompt: version.prompt,
      updated_at: new Date().toISOString(),
    })
    .eq('id', promptId)

  if (updateError) {
    throw new Error(`Erro ao restaurar versão: ${updateError.message}`)
  }

  const { data: currentRow } = await supabase
    .from('ai_agent_prompts')
    .select('current_version')
    .eq('id', promptId)
    .single()

  const newVersionNumber = (currentRow?.current_version || 0) + 1

  await createPromptVersion(
    promptId,
    newVersionNumber,
    version.title,
    version.description,
    version.prompt,
    `Restaurado da versão ${versionNumber}`
  )

  await supabase
    .from('ai_agent_prompts')
    .update({ current_version: newVersionNumber })
    .eq('id', promptId)
}

export async function listPrompts(
  filters: PromptAgenteFilters = {},
  page: number = 1,
  limit: number = 25
): Promise<{
  prompts: PromptAgente[]
  total: number
  page: number
  limit: number
  totalPages: number
}> {
  const sortBy = filters.sort_by || 'updated_at'
  const ascending = filters.sort_order === 'asc'

  let query = supabase
    .from('ai_agent_prompts')
    .select('*')
    .order(sortBy, { ascending })

  let countQuery = supabase
    .from('ai_agent_prompts')
    .select('*', { count: 'exact', head: true })

  if (filters.query?.trim()) {
    const q = filters.query.trim()
    const orFilter = `title.ilike.%${q}%,description.ilike.%${q}%,prompt.ilike.%${q}%,model.ilike.%${q}%,creativity.ilike.%${q}%`
    query = query.or(orFilter)
    countQuery = countQuery.or(orFilter)
  }

  const offset = (page - 1) * limit
  const { count: totalCount } = await countQuery
  const { data, error } = await query.range(offset, offset + limit - 1)

  if (error) {
    throw new Error(`Erro ao carregar prompts: ${error.message}`)
  }

  const prompts = (data || []) as PromptAgente[]
  const total = totalCount || 0

  return {
    prompts,
    total,
    page,
    limit,
    totalPages: Math.ceil(total / limit) || 0,
  }
}

export async function getPrompt(id: string): Promise<PromptAgente | null> {
  const { data, error } = await supabase
    .from('ai_agent_prompts')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw new Error(`Erro ao carregar prompt: ${error.message}`)
  }

  return data as PromptAgente
}

export async function createPrompt(payload: CreatePromptAgentePayload): Promise<string> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  const { data, error } = await supabase
    .from('ai_agent_prompts')
    .insert({
      title: payload.title.trim(),
      description: payload.description.trim() || null,
      prompt: payload.prompt,
      model: payload.model.trim() || null,
      creativity: payload.creativity.trim() || null,
      created_by: user?.id ?? null,
      current_version: 0,
    })
    .select('id')
    .single()

  if (error || !data) {
    throw new Error(`Erro ao criar prompt: ${error?.message || 'Erro desconhecido'}`)
  }

  await createPromptVersion(
    data.id,
    1,
    payload.title.trim(),
    payload.description.trim() || null,
    payload.prompt,
    'Versão inicial'
  )

  await supabase.from('ai_agent_prompts').update({ current_version: 1 }).eq('id', data.id)

  return data.id
}

export async function updatePrompt(payload: UpdatePromptAgentePayload): Promise<void> {
  const { error } = await supabase
    .from('ai_agent_prompts')
    .update({
      title: payload.title.trim(),
      description: payload.description.trim() || null,
      prompt: payload.prompt,
      model: payload.model.trim() || null,
      creativity: payload.creativity.trim() || null,
    })
    .eq('id', payload.id)

  if (error) {
    throw new Error(`Erro ao atualizar prompt: ${error.message}`)
  }

  const { data: lastVersion } = await supabase
    .from('ai_agent_prompt_versions')
    .select('version_number')
    .eq('prompt_id', payload.id)
    .order('version_number', { ascending: false })
    .limit(1)
    .maybeSingle()

  const newVersionNumber = (lastVersion?.version_number || 0) + 1

  await createPromptVersion(
    payload.id,
    newVersionNumber,
    payload.title.trim(),
    payload.description.trim() || null,
    payload.prompt,
    'Atualização do prompt'
  )

  await supabase
    .from('ai_agent_prompts')
    .update({ current_version: newVersionNumber })
    .eq('id', payload.id)
}

export async function deletePrompt(id: string): Promise<void> {
  const { error: versionsError } = await supabase
    .from('ai_agent_prompt_versions')
    .delete()
    .eq('prompt_id', id)

  if (versionsError) {
    throw new Error(`Erro ao excluir versões: ${versionsError.message}`)
  }

  const { error } = await supabase.from('ai_agent_prompts').delete().eq('id', id)

  if (error) {
    throw new Error(`Erro ao excluir prompt: ${error.message}`)
  }
}

export const promptsAgentesService = {
  listPrompts,
  getPrompt,
  createPrompt,
  updatePrompt,
  deletePrompt,
  getPromptVersions,
  restorePromptVersion,
}
